import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { dashboardApi, usersApi } from '../api/endpoints';
import { pickQuote, getGreeting } from '../lib/quotes';
import SoundNotifier from '../components/SoundNotifier';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const THEMES = {
  red:     { bar: 'bg-rose-500',    chip: 'bg-rose-500 text-white',    text: 'text-rose-600',    border: 'border-rose-200 hover:border-rose-300' },
  orange:  { bar: 'bg-orange-500',  chip: 'bg-orange-500 text-white',  text: 'text-orange-600',  border: 'border-orange-200 hover:border-orange-300' },
  purple:  { bar: 'bg-purple-500',  chip: 'bg-purple-500 text-white',  text: 'text-purple-600',  border: 'border-purple-200 hover:border-purple-300' },
  blue:    { bar: 'bg-blue-500',    chip: 'bg-blue-500 text-white',    text: 'text-blue-600',    border: 'border-blue-200 hover:border-blue-300' },
  green:   { bar: 'bg-emerald-500', chip: 'bg-emerald-500 text-white', text: 'text-emerald-600', border: 'border-emerald-200 hover:border-emerald-300' },
  cyan:    { bar: 'bg-cyan-500',    chip: 'bg-cyan-500 text-white',    text: 'text-cyan-600',    border: 'border-cyan-200 hover:border-cyan-300' },
  darkred: { bar: 'bg-red-900',     chip: 'bg-red-900 text-white',     text: 'text-red-900',     border: 'border-red-300 hover:border-red-400' },
  yellow:  { bar: 'bg-yellow-400',  chip: 'bg-yellow-400 text-yellow-950', text: 'text-yellow-600', border: 'border-yellow-200 hover:border-yellow-300' },
  indigo:  { bar: 'bg-indigo-600',  chip: 'bg-indigo-600 text-white',  text: 'text-indigo-700',  border: 'border-indigo-200 hover:border-indigo-300' },
};

const CARD_DEFS = [
  { key: 'hotReminders', title: "Today's Reminder – HOT Leads", sub: 'HOT leads due for follow-up', icon: 'Flame', theme: 'red' },
  { key: 'interestedReminders', title: "Today's Reminder – Interested Leads", sub: 'Interested leads due for follow-up', icon: 'Sparkles', theme: 'orange' },
  { key: 'avgSiteVisitReminders', title: "Today's Reminder – Avg Customer Site Visit", sub: 'Status: Avg Customer Site Visit', icon: 'UserCheck', theme: 'purple' },
  { key: 'siteVisitCompletedReminders', title: "Today's Reminder – Site Visit Completed", sub: 'Status: Site Visit Completed', icon: 'CheckCircle2', theme: 'blue' },
  { key: 'scheduledSiteVisitReminders', title: "Today's Reminder – Scheduled Site Visits", sub: 'Status: Scheduled Site Visits', icon: 'CalendarCheck', theme: 'green' },
  { key: 'allLeadReminders', title: "Today's Reminder – All Leads", sub: 'All pending lead reminders (carries forward until actioned)', icon: 'BellRing', theme: 'indigo' },
  { key: 'adCampaignLeads', title: 'AD Campaign Leads', sub: 'New leads from ad sources', icon: 'Megaphone', theme: 'cyan', wide: true },
  { key: 'missedFollowups', title: "Today's Missed Follow-ups", sub: 'Due but still pending', icon: 'AlertTriangle', theme: 'darkred' },
  { key: 'todaysTasks', title: "Today's Tasks", sub: 'Assigned tasks', icon: 'ListChecks', theme: 'yellow' },
];

const RANGE_PRESETS = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'week', label: 'This Week' },
  { value: 'month', label: 'This Month' },
  { value: 'custom', label: 'Custom Date Range' },
];

const fmt = (d) => {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

function rangeFor(preset, custom) {
  const now = new Date();
  if (preset === 'yesterday') {
    const y = new Date(now); y.setDate(y.getDate() - 1);
    const t = fmt(y); return { from: t, to: t };
  }
  if (preset === 'week') {
    const s = new Date(now); s.setDate(s.getDate() - ((s.getDay() + 6) % 7));
    return { from: fmt(s), to: fmt(now) };
  }
  if (preset === 'month') {
    return { from: fmt(new Date(now.getFullYear(), now.getMonth(), 1)), to: fmt(now) };
  }
  if (preset === 'custom' && custom.from) {
    return { from: custom.from, to: custom.to || custom.from };
  }
  const t = fmt(now);
  return { from: t, to: t };
}

const DashCard = React.memo(({ def, card, onClick }) => {
  const t = THEMES[def.theme];
  const Icon = Icons[def.icon] || Icons.Activity;
  return (
    <Card
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
      data-testid={`dashboard-card-${def.key}`}
      className={`relative overflow-hidden cursor-pointer rounded-xl border ${t.border} shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-[transform,box-shadow,border-color] duration-200 group outline-none focus-visible:ring-2 focus-visible:ring-brand-navy/40 ${def.wide ? 'sm:col-span-2' : ''}`}
    >
      <div className={`absolute inset-y-0 left-0 w-1.5 ${t.bar}`} />
      <CardContent className="p-4 sm:p-5 pl-5 sm:pl-6 flex flex-col h-full">
        <div className="flex items-start justify-between gap-3">
          <div className={`w-10 h-10 rounded-lg ${t.chip} flex items-center justify-center shrink-0`}>
            <Icon className="w-5 h-5" />
          </div>
          <div className={`font-display text-3xl font-bold ${t.text}`} data-testid={`dashboard-count-${def.key}`}>
            {card?.count ?? 0}
          </div>
        </div>
        <div className="mt-3 text-sm font-semibold text-slate-800 leading-snug">{def.title}</div>
        <div className="text-[11px] text-slate-500 mt-0.5">{def.sub}</div>
        {card?.carriedForward > 0 && (
          <div className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 self-start" data-testid={`carried-forward-${def.key}`}>
            <Icons.History className="w-3 h-3" /> {card.carriedForward} carried forward
          </div>
        )}

        {def.key === 'adCampaignLeads' && (
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-1.5">
            {(card?.bySource || []).map(s => (
              <div key={s.source} className="flex items-center justify-between text-[11px] bg-cyan-50/70 border border-cyan-100 rounded px-2 py-1" data-testid={`ad-source-${s.source.replace(/[^a-zA-Z]+/g, '-')}`}>
                <span className="text-slate-600 truncate">{s.source}</span>
                <span className="font-bold text-cyan-700 ml-1">{s.count}</span>
              </div>
            ))}
          </div>
        )}

        {def.key === 'todaysTasks' && (
          <div className="mt-3 flex flex-wrap gap-2">
            <span className="text-[11px] font-semibold bg-amber-100 text-amber-800 rounded-full px-2.5 py-1" data-testid="tasks-pending-count">Pending: {card?.pending ?? 0}</span>
            <span className="text-[11px] font-semibold bg-emerald-100 text-emerald-800 rounded-full px-2.5 py-1" data-testid="tasks-completed-count">Completed: {card?.completed ?? 0}</span>
          </div>
        )}

        <div className="mt-auto pt-3 flex items-center text-[11px] font-medium text-slate-400 group-hover:text-slate-700 transition-colors">
          View list <Icons.ArrowRight className="w-3 h-3 ml-1 group-hover:translate-x-0.5 transition-transform" />
        </div>
      </CardContent>
    </Card>
  );
});
DashCard.displayName = 'DashCard';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [preset, setPreset] = useState('today');
  const [custom, setCustom] = useState({ from: '', to: '' });
  const [lastUpdated, setLastUpdated] = useState(null);
  const [userFilter, setUserFilter] = useState('all');
  const [teamUsers, setTeamUsers] = useState([]);
  const [quote] = useState(pickQuote);
  const greeting = getGreeting();
  const GreetIcon = Icons[greeting.icon] || Icons.Sun;

  const isAdmin = hasPermission('admin', 'view');
  const range = useMemo(() => rangeFor(preset, custom), [preset, custom]);

  useEffect(() => {
    if (!isAdmin) return;
    usersApi.options().then(setTeamUsers).catch(() => {});
  }, [isAdmin]);

  const load = useCallback(async (silent = false) => {
    try {
      const params = { date_from: range.from, date_to: range.to };
      if (userFilter !== 'all') params.user_id = userFilter;
      const d = await dashboardApi.cards(params);
      setData(d);
      setLastUpdated(new Date());
    } catch (e) {
      if (!silent) toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to, userFilter]);

  useEffect(() => {
    load();
    const t = setInterval(() => load(true), 60000);
    return () => clearInterval(t);
  }, [load]);

  const onCardClick = useCallback((def) => {
    const card = data?.cards?.[def.key];
    if (def.key === 'todaysTasks') {
      navigate('/tasks', { state: preset === 'today' ? { filterDate: 'today' } : {} });
      return;
    }
    navigate('/leads', {
      state: { filterIds: card?.leadIds || [], filterLabel: def.title },
    });
  }, [data, navigate, preset]);

  const rangeLabel = RANGE_PRESETS.find(r => r.value === preset)?.label || 'Today';

  return (
    <div className="space-y-5" data-testid="dashboard-page">
      <SoundNotifier signalValue={data?.cards?.todaysTasks?.pending || 0} />

      {/* Header: title + date filter + last updated */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-display text-2xl font-bold text-slate-900 flex items-center gap-2" data-testid="dashboard-greeting">
            <GreetIcon className="w-6 h-6 text-brand-gold shrink-0" />
            {greeting.text}{user?.name ? `, ${user.name.split(' ')[0]}` : ''}!
          </h1>
          <div className="text-xs text-slate-500 mt-0.5">
            {rangeLabel} · {range.from === range.to ? range.from : `${range.from} → ${range.to}`}
            {data?.scopedTo && isAdmin ? ` · Viewing: ${data.scopedTo}` : ''}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[11px] text-slate-500" data-testid="dashboard-last-updated">
            Last updated: {lastUpdated ? lastUpdated.toLocaleTimeString() : '—'}
          </span>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => load()} title="Refresh" data-testid="dashboard-refresh-btn">
            <Icons.RefreshCw className="w-4 h-4" />
          </Button>
          {isAdmin && (
            <Select value={userFilter} onValueChange={setUserFilter}>
              <SelectTrigger className="w-[170px] h-9 text-xs font-bold uppercase" data-testid="dashboard-user-filter">
                <Icons.UserRound className="w-3.5 h-3.5 mr-1.5 text-slate-500" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="user-filter-all">All Users</SelectItem>
                {teamUsers.map(u => (
                  <SelectItem key={u.id} value={u.id} data-testid={`user-filter-${u.id}`}>{u.name} — {u.role}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Select value={preset} onValueChange={setPreset}>
            <SelectTrigger className="w-[170px] h-9 text-xs font-bold uppercase" data-testid="dashboard-date-filter">
              <Icons.CalendarDays className="w-3.5 h-3.5 mr-1.5 text-slate-500" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RANGE_PRESETS.map(r => (
                <SelectItem key={r.value} value={r.value} data-testid={`date-filter-${r.value}`}>{r.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {preset === 'custom' && (
            <div className="flex items-center gap-1.5" data-testid="dashboard-custom-range">
              <Input type="date" className="h-9 w-[140px] text-xs" value={custom.from} onChange={e => setCustom(c => ({ ...c, from: e.target.value }))} data-testid="custom-date-from" />
              <span className="text-slate-400 text-xs">to</span>
              <Input type="date" className="h-9 w-[140px] text-xs" value={custom.to} onChange={e => setCustom(c => ({ ...c, to: e.target.value }))} data-testid="custom-date-to" />
            </div>
          )}
        </div>
      </div>

      {/* Motivational quote — fresh on every reload */}
      <div className="flex items-start gap-3 rounded-xl border border-brand-gold/30 bg-gradient-to-r from-amber-50/80 to-white px-4 py-3" data-testid="dashboard-quote">
        <Icons.Quote className="w-5 h-5 text-brand-gold shrink-0 mt-0.5" />
        <div className="min-w-0">
          <div className="text-sm font-medium text-slate-800 italic leading-snug">“{quote}”</div>
        </div>
      </div>

      {/* 8 dashboard cards */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => <Skeleton key={i} className="h-44 w-full rounded-xl" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {CARD_DEFS.map(def => (
            <DashCard
              key={def.key}
              def={def}
              card={data?.cards?.[def.key]}
              onClick={() => onCardClick(def)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Dashboard;
